/**
 * Service Layer - Media Service
 * Handles camera, microphone, and screen sharing logic
 * Uses Core layer for WebRTC operations
 * Updates State layer (store)
 */

import MicrophoneStream from 'microphone-stream';
import {isIOS} from "react-device-detect";
import {
    compressAudio,
    combineArrayBuffers,
    float32ToInt16,
    numberToArrayBuffer,
    reduceNoise,
    stringToArrayBuffer
} from '../core/webrtc/AudioProcessor';
import {DEFAULT_SAMPLE_RATE} from '../core/constants/RtcEnums';

export class MediaService {
    constructor(store, socketManager) {
        this.store = store;
        this.socketManager = socketManager;
        this.micStream = null;
    }

    /**
     * Get user media stream (camera/mic)
     */
    async getUserMedia(constraints) {
        try {
            return await navigator.mediaDevices.getUserMedia(constraints);
        } catch (error) {
            console.error('Error getting user media:', error);
            throw error;
        }
    }

    /**
     * Get display media stream (screen share)
     */
    async getDisplayMedia(constraints = {video: {width: {max: 1280}}}) {
        try {
            return await navigator.mediaDevices.getDisplayMedia(constraints);
        } catch (error) {
            console.error('Error getting display media:', error);
            throw error;
        }
    }

    /**
     * Start camera
     */
    async startCamera(selectedCamera, forcedFacingMode) {
        const constraints = {
            video: forcedFacingMode
                ? {facingMode: forcedFacingMode}
                : selectedCamera
                    ? {deviceId: {exact: selectedCamera}}
                    : true,
            audio: false
        };

        const stream = await this.getUserMedia(constraints);
        const videoTrack = stream.getVideoTracks()[0];

        // Cleanup old track
        if (this.store.cameraTrack) {
            this.store.cameraTrack.stop();
        }

        this.store.cameraTrack = videoTrack;
        this.store.isCameraWasOn = true;

        // Create or update RTC stream
        if (this.store.rtcStream) {
            // Remove old video tracks
            this.store.rtcStream.getVideoTracks().forEach(track => {
                this.store.rtcStream.removeTrack(track);
                track.stop();
            });
            this.store.rtcStream.addTrack(videoTrack);
        } else {
            this.store.rtcStream = new MediaStream([videoTrack]);
        }

        return videoTrack;
    }

    /**
     * Stop camera
     */
    stopCamera() {
        if (this.store.cameraTrack) {
            this.store.cameraTrack.stop();
            this.store.cameraTrack = null;
        }

        if (this.store.rtcStream) {
            this.store.rtcStream.getVideoTracks().forEach(track => {
                this.store.rtcStream.removeTrack(track);
                track.stop();
            });
        }

        this.store.isCameraWasOn = false;
    }

    /**
     * Start screen sharing
     */
    async startScreenShare() {
        try {
            // Cleanup old screen share track
            if (this.store.screenShareTrack) {
                this.store.screenShareTrack.stop();
            }

            // Stop camera if running
            if (this.store.cameraTrack) {
                this.stopCamera();
            }

            const screenStream = await this.getDisplayMedia();
            const screenTrack = screenStream.getVideoTracks()[0];

            this.store.screenShareTrack = screenTrack;
            this.store.rtcStream = new MediaStream([screenTrack]);

            // Add audio track if mic is on
            if (this.store.micOn && this.store.audioTrack) {
                this.store.rtcStream.addTrack(this.store.audioTrack);
            }

            // Update camera dimensions for screen share
            this.store.cameraWidth = 1280;
            this.store.cameraHeight = 720;

            // Notify other participants
            this.socketManager.notifyShareScreenStarted(this.store.roomId, this.store.deviceId);

            return screenTrack;
        } catch (error) {
            console.error('Error starting screen share:', error);
            // Restore camera if screen share failed and camera was on
            if (this.store.isCameraWasOn) {
                this.store.cameraOn = true;
            }
            throw error;
        }
    }

    /**
     * Stop screen sharing
     */
    stopScreenShare() {
        if (this.store.screenShareTrack) {
            this.store.screenShareTrack.stop();
            this.store.screenShareTrack = null;
        }

        // Restore camera dimensions
        const isPortrait = window.screen.height > window.screen.width;
        this.store.cameraWidth = isPortrait ? 480 : 640;
        this.store.cameraHeight = isPortrait ? 640 : 480;
    }

    /**
     * Start microphone
     */
    async startMicrophone(selectedMic) {
        const constraints = {
            audio: selectedMic ? {deviceId: {exact: selectedMic}} : true
        };

        const stream = await this.getUserMedia(constraints);
        const audioTrack = stream.getAudioTracks()[0];

        // Cleanup old track
        if (this.store.audioTrack) {
            this.store.audioTrack.stop();
        }

        this.store.audioTrack = audioTrack;

        // Add to RTC stream
        if (this.store.rtcStream) {
            // Remove old audio tracks
            this.store.rtcStream.getAudioTracks().forEach(track => {
                this.store.rtcStream.removeTrack(track);
                track.stop();
            });
            this.store.rtcStream.addTrack(audioTrack);
        } else {
            this.store.rtcStream = new MediaStream([audioTrack]);
        }

        return audioTrack;
    }

    /**
     * Stop microphone
     */
    stopMicrophone() {
        if (this.store.audioTrack) {
            this.store.audioTrack.stop();
            this.store.audioTrack = null;
        }

        if (this.store.rtcStream) {
            this.store.rtcStream.getAudioTracks().forEach(track => {
                this.store.rtcStream.removeTrack(track);
                track.stop();
            });
        }
    }

    /**
     * Register audio input processing
     */
    registerAudioInput() {
        if (!this.store.rtcStream || !this.store.micOn) {
            console.error('RTC stream is not available or mic is off.');
            return;
        }

        // Cleanup previous mic stream
        if (this.micStream) {
            this.micStream.stop();
        }

        this.micStream = new MicrophoneStream({bufferSize: 4096});
        this.micStream.setStream(this.store.rtcStream);

        this.micStream.on('data', (chunk) => {
            const float32Array = MicrophoneStream.toRaw(chunk);
            this.processMicInput(float32Array);
        });

        this.micStream.on('end', () => {
            console.log('Microphone stream ended.');
        });

        this.micStream.on('error', (error) => {
            console.error('Error with microphone stream:', error);
        });
    }

    /**
     * Process microphone input and send to server
     */
    processMicInput(float32Array) {
        if (!this.store.micOn || !reduceNoise(float32Array)) {
            return;
        }

        // Convert and compress audio
        const int16Array = float32ToInt16(float32Array);
        const zippedSound = compressAudio(int16Array.buffer);

        // Create header
        const frequencyArrayBuffer = numberToArrayBuffer(this.store.sampleRate || DEFAULT_SAMPLE_RATE);
        const firstZeroArrayBuffer = new Uint8Array(1);
        firstZeroArrayBuffer[0] = 0;

        const soundDataWithHeader = combineArrayBuffers([
            firstZeroArrayBuffer.buffer,
            frequencyArrayBuffer,
            zippedSound,
        ]);

        // Add frame info
        const frameInfoStr = JSON.stringify({sid: this.store.deviceId});
        const frameInfoArrayBuffer = stringToArrayBuffer(frameInfoStr);
        const frameInfoSize = frameInfoArrayBuffer.byteLength;
        const frameInfoSizeBuffer = numberToArrayBuffer(frameInfoSize);

        const finalArrayBuffer = combineArrayBuffers([
            frameInfoSizeBuffer,
            frameInfoArrayBuffer,
            soundDataWithHeader
        ]);

        // Send via socket
        this.socketManager.sendAudioData(this.store.roomId, finalArrayBuffer);
    }

    /**
     * Unregister audio input processing
     */
    unregisterAudioInput() {
        if (this.micStream) {
            this.micStream.stop();
            this.micStream = null;
        }
    }

    /**
     * Enumerate devices (cameras, mics, speakers)
     */
    async enumerateDevices() {
        try {
            const devices = await navigator.mediaDevices.enumerateDevices();
            return {
                cameras: devices.filter(d => d.kind === 'videoinput'),
                microphones: devices.filter(d => d.kind === 'audioinput'),
                speakers: devices.filter(d => d.kind === 'audiooutput'),
            };
        } catch (error) {
            console.error('Error enumerating devices:', error);
            return {cameras: [], microphones: [], speakers: []};
        }
    }

    /**
     * Switch camera (front/back on mobile)
     */
    async switchCamera() {
        const {cameras} = await this.enumerateDevices();

        if (cameras.length < 2) {
            console.warn('Only one camera available');
            return;
        }

        // For Android
        if (cameras[0].label.includes('camera2')) {
            cameras.sort((a, b) => {
                const numA = parseInt(a.label.match(/camera2\s+(\d+)/)[1]);
                const numB = parseInt(b.label.match(/camera2\s+(\d+)/)[1]);
                return numA - numB;
            });

            const frontCamera = cameras.find(camera => camera.label.includes('front'));
            const backCamera = cameras.find(camera => camera.label.includes('back'));
            const currentIsBack = this.store.selectedCamera === backCamera?.deviceId;

            if (currentIsBack && frontCamera) {
                this.store.selectedCamera = frontCamera.deviceId;
            } else if (backCamera) {
                this.store.selectedCamera = backCamera.deviceId;
            }
        }
        // For iOS
        else if (isIOS) {
            if (this.store.forcedFacingMode === 'user' || !this.store.forcedFacingMode) {
                this.store.forcedFacingMode = 'environment';
            } else {
                this.store.forcedFacingMode = 'user';
            }
        }
    }

    /**
     * Cleanup all tracks
     */
    cleanupAllTracks() {
        this.stopCamera();
        this.stopScreenShare();
        this.stopMicrophone();
        this.unregisterAudioInput();

        if (this.store.rtcStream) {
            this.store.rtcStream.getTracks().forEach(track => track.stop());
            this.store.rtcStream = null;
        }
    }
}
